import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PodiumCard } from "./PodiumCard";
import { StartLights } from "./StartLights";
import { ConfettiLayer } from "../effects/ConfettiLayer";
import { TiresLayer } from "../effects/TiresLayer";
import { CarRow } from "../cars/CarRow";
import { CountdownGate } from "../timers/CountdownGate";
import { Trophy } from "lucide-react";

export const Podium = ({
  top3 = [],
  unlockAt,            // Date | number(ms)
  countdownSeconds,    // number (s)
  fetchTop3,           // async () => Driver[],
  view = 'podium',
  finishSession = false
}) => {


  console.log(unlockAt)
  // ----- CONFIG PIN -----
  const CEREMONY_PIN = "1234";

  // ---- hooks al principio
  const [phase, setPhase] = useState("idle");     // idle | lights | thanks | boom | show
  const [confetti, setConfetti] = useState(false);
  const [tires, setTires] = useState(false);
  const [muted, setMuted] = useState(false);

  // PIN modal state
  const [pinOpen, setPinOpen] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState("");

  // manejo de datos remotos
  const [drivers, setDrivers] = useState(null);   // null = aún no cargado
  const [loadingData, setLoadingData] = useState(false);
  const [loadError, setLoadError] = useState("");

  const audioCtxRef = useRef(null);
  const timersRef = useRef([]);

  // ---- gate de cuenta regresiva
  const targetMs = useMemo(() => {
    if (unlockAt) return unlockAt instanceof Date ? unlockAt.getTime() : Number(unlockAt);
    if (countdownSeconds) return Date.now() + countdownSeconds * 1000;
    return null; // 👈 nada hasta que tengamos fecha
  }, [unlockAt, countdownSeconds]);

  const [gateOpen, setGateOpen] = useState(false);

  // recalcular apenas tengamos unlockAt
  useEffect(() => {
    if (targetMs) {
      setGateOpen(Date.now() >= targetMs);
    }
  }, [targetMs]);

  // -------- Utils de sonido --------
  const ensureCtx = () => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = AC ? new AC() : null;
    }
    return audioCtxRef.current;
  };

  const beep = (freq = 880, duration = 0.12, volume = 0.06) => {
    if (muted) return;
    const ctx = ensureCtx(); if (!ctx) return;
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.type = "square"; osc.frequency.value = freq;
    osc.connect(gain); gain.connect(ctx.destination); gain.gain.value = volume;
    const t = ctx.currentTime; osc.start(t); osc.stop(t + duration);
  };

  const engineRev = () => {
    if (muted) return;
    const ctx = ensureCtx(); if (!ctx) return;
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.type = "sawtooth"; osc.connect(gain); gain.connect(ctx.destination);
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0.04, t);
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.exponentialRampToValueAtTime(420, t + 0.22);
    osc.frequency.exponentialRampToValueAtTime(180, t + 0.95);
    osc.start(t); osc.stop(t + 1.1);
  };

  // ---- ceremonia
  const afterLights = () => {
    setPhase("thanks");
    timersRef.current.push(setTimeout(() => setPhase("boom"), 2000));
    timersRef.current.push(setTimeout(() => { setConfetti(true); setTires(false); setPhase("show"); engineRev(); }, 3500));
    timersRef.current.push(setTimeout(() => { setConfetti(true); setTires(false); }, 10000));
  };

  // Carga de datos (se llama solo tras pasar PIN)
  const loadDataIfNeeded = async () => {
    setLoadingData(true);
    setLoadError("");
    try {
      let data = top3;
      if (typeof fetchTop3 === "function") {
        const result = await Promise.race([
          fetchTop3(),
          new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 10000))
        ]);
        if (Array.isArray(result) && result.length >= 3) data = result;
      }
      setDrivers(data);
    } catch (e) {
      setLoadError("No pudimos obtener los tiempos. Usaremos datos locales.");
      setDrivers(top3);
    } finally {
      setLoadingData(false);
    }
  };

  // Arranque de luces (se llama después de cargar datos)
  const runCeremony = () => {
    // limpiar timers previos
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setPhase("lights");


  };

  // Botón Start -> abrir modal PIN (si idle)
  const onClickStart = () => {
    if (phase !== "idle") return;
    setPinValue("");
    setPinError("");
    setPinOpen(true);
  };

  // Confirmar PIN en modal
  // const confirmPin = async () => {
  //   if (pinValue.trim() !== CEREMONY_PIN) {
  //     setPinError("PIN incorrecto");
  //     return;
  //   }
  //   setPinOpen(false);
  //   await loadDataIfNeeded();
  //   runCeremony();
  // };

  // Confirmar PIN en modal
  const confirmPin = async () => {
    try {
      const res = await fetch("https://middleware-lite.hitch.cl/api/settings/finish-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinValue }), // 👈 mandamos el PIN
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setPinError(data.message || "PIN incorrecto");
        return;
      }

      setPinOpen(false);
    } catch (err) {
      setPinError("No se pudo validar contraseña");
      console.error("Error actualizando finishSession:", err);
      return;
    }

    await loadDataIfNeeded();
    runCeremony();
  };


  // Enter para confirmar en modal
  const onPinKey = (e) => {
    if (e.key === "Enter") confirmPin();
  };

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const arranged = React.useMemo(() => {
    const src = drivers || top3;
    if (!src || src.length < 3) return [];
    return [src[1], src[0], src[2]]; // 2º – 1º – 3º
  }, [drivers, top3]);

  // -------- gating por cuenta regresiva --------
  if (!gateOpen) {
    const onTick = (s) => {
      if (s > 10 || s === 0) return;
      const ctx = ensureCtx(); if (!ctx) return;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 740; g.gain.value = 0.04;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime; o.start(t); o.stop(t + 0.08);
    };
    return (
      <CountdownGate
        target={targetMs}
        title="La ceremonia de premiación comienza en"
        onEnd={() => setGateOpen(true)}
        onTick={onTick}
      />
    );
  }
  const podiumDrivers = (drivers || top3 || []).slice(0, 3);

  // -------- UI --------
  return (
    <section id="podium" className="relative w-full py-14">
      {confetti && <ConfettiLayer continuous pieces={220} />}
      {tires && <TiresLayer />}

      <div className="mx-auto max-w-7xl rounded-3xl border border-white/10 bg-gradient-to-b from-slate-900/80 to-black/80 p-10 shadow-[0_20px_80px_rgba(0,0,0,0.6)] relative overflow-hidden">
        <div className="flex items-center justify-center mb-8">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            <span className="text-white">CEREMONIA DE </span>
            <span className="text-hitch">PREMIACIÓN</span>
          </h2>
        </div>

        {/* PENDIENTE / PARC FERMÉ (visible mientras phase === 'idle') */}
        {phase === "idle" && !finishSession ? (
          <div className="relative rounded-2xl border border-white/10 bg-black/40 p-8">
            <div className="flex items-center gap-3 mb-4">
              <Trophy className="w-6 h-6 text-yellow-300" />
              <span className="text-white/90 font-semibold">Podio bloqueado</span>
            </div>

            {loadError && (
              <div className="mb-4 text-sm text-amber-300/90">
                {loadError}
              </div>
            )}

            {/* skeleton de columnas */}
            <div className="grid grid-cols-3 gap-6 mt-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-white/5 animate-pulse mb-3" />
                  <div className={`w-28 rounded-t-xl bg-gradient-to-b from-white/10 to-white/5`}
                    style={{ height: i === 2 ? 220 : i === 1 ? 180 : 160 }} />
                </div>
              ))}
            </div>

            <p className="mt-6 text-center text-white/60 text-sm">
              Presiona <span className="text-white font-semibold">Obtener Ganadores</span>, ingresa el PIN y comenzará la presentación.
            </p>

            <div className="text-center gap-3 mt-4">
              <button
                className="px-4 py-2 rounded-lg bg-hitch hover:bg-red-500 text-white text-sm font-semibold shadow disabled:opacity-50"
                onClick={onClickStart}
                disabled={phase !== "idle" || loadingData}
              >
                {loadingData ? "Cargando..." : "Obtener Ganadores"}
              </button>
              {/* <button
                className="ml-3 px-3 py-2 rounded-lg bg-gray-800 text-white text-xs border border-gray-700 hover:bg-gray-700"
                onClick={() => setMuted((m) => !m)}
              >
                {muted ? "🔇" : "🔊"}
              </button> */}
            </div>
          </div>
        ) : (
          <div className="relative mx-auto flex items-end justify-center gap-8 md:gap-12">
            <div className="absolute inset-x-10 bottom-0 h-20 rounded-full bg-black/50 blur-2xl" />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: phase === "show" || phase === "idle" ? 1 : 0, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`${phase === "show" || phase === "idle" ? "pointer-events-auto" : "pointer-events-none"}`}
            >

              <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 w-full">
                {/* 1er lugar */}
                {podiumDrivers[0] && (
                  <div className="order-1 md:order-2">
                    <PodiumCard driver={podiumDrivers[0]} place={0} />
                  </div>
                )}

                {/* 2do lugar */}
                {podiumDrivers[1] && (
                  <div className="order-2 md:order-1">
                    <PodiumCard driver={podiumDrivers[1]} place={1} />
                  </div>
                )}

                {/* 3er lugar */}
                {podiumDrivers[2] && (
                  <div className="order-3 md:order-3">
                    <PodiumCard driver={podiumDrivers[2]} place={2} />
                  </div>
                )}
              </div>




              <div className="mt-6 text-xl md:text-3xl font-black tracking-wider text-white text-center">CARRERA FINALIZADA </div>
              <div className="mt-6 text-xl md:text-3xl font-black tracking-wider text-hitch text-center">¡GRACIAS POR PARTICIPAR! </div>
            </motion.div>
          </div>
        )}

        {/* Overlays de luces/thanks/boom */}
        <AnimatePresence>
          {phase !== "idle" && phase !== "show" && (
            <motion.div
              key={phase}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm"
            >
              <CarRow />
              {phase === "lights" ? (
                <StartLights onComplete={afterLights} onBeep={() => beep(880, 0.12, 0.06)} />
              ) : phase === "thanks" ? (
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center">
                  <div className="text-3xl md:text-5xl font-extrabold text-white">¡Gracias a todo el equipo y a los participantes!</div>
                  <div className="mt-2 text-sm md:text-base text-white/70">Hitch Integradores - Solution Day 2025 🏁</div>
                </motion.div>
              ) : (
                <>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }} className="absolute inset-0 bg-white/40" />
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 12, opacity: 0 }} transition={{ duration: 0.6, ease: "easeOut" }} className="w-24 h-24 rounded-full border-8 border-red-500/80" />
                  <div className="mt-6 text-xl md:text-3xl font-black tracking-wider text-hitch">GRACIAS POR PARTICIPAR! 🟥🟥🟥🟥🟥</div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ---- MODAL PIN ---- */}
      <AnimatePresence>
        {pinOpen && (
          <motion.div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-full max-w-sm mx-4 rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-black p-6"
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
            >
              <h3 className="text-lg font-semibold text-white mb-2">PIN requerido</h3>
              <p className="text-sm text-white/60 mb-4">Ingresa el PIN para obtener ganadores.</p>
              <input
                type="password"
                inputMode="numeric"
                pattern="\d*"
                maxLength={8}
                autoFocus
                value={pinValue}
                onChange={(e) => { setPinValue(e.target.value); setPinError(""); }}
                onKeyDown={onPinKey}
                className="w-full px-4 py-2 rounded-lg bg-black border border-white/10 text-white outline-none focus:border-red-500"
                placeholder="••••"
              />
              {pinError && <div className="mt-2 text-sm text-hitch">{pinError}</div>}

              <div className="mt-4 flex gap-2 justify-end">
                <button
                  onClick={() => setPinOpen(false)}
                  className="px-3 py-2 text-sm rounded-md bg-gray-800 border border-white/10 text-white hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmPin}
                  className="px-4 py-2 text-sm rounded-md bg-hitch text-white hover:bg-red-500"
                >
                  Confirmar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
